import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Crosshair,
  Activity,
  Gauge,
  Rocket,
  Milestone,
  User,
  Settings,
  Trophy,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Flame,
  Zap,
} from "lucide-react";
import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { WarRoomBackground } from "@/components/strangelove/WarRoomBackground";
import { useDashboardStats } from "@/hooks/use-mock-data";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "The Big Board" },
  { to: "/opportunities", icon: Crosshair, label: "Opportunities" },
  { to: "/pain-analytics", icon: Activity, label: "Pain Analytics" },
  { to: "/revenue", icon: Gauge, label: "Doomsday Machine" },
  { to: "/ventures", icon: Rocket, label: "Ventures" },
  { to: "/milestones", icon: Milestone, label: "Milestones" },
  { to: "/profile", icon: User, label: "Profile" },
  { to: "/settings", icon: Settings, label: "Settings" },
  { to: "/achievements", icon: Trophy, label: "Trophy Room" },
];

const SIDEBAR_KEY = "strangelove-sidebar-collapsed";

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_KEY) === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { data: stats } = useDashboardStats();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const sidebarWidth = collapsed ? 64 : 240;

  const sidebarContent = (isMobile = false) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn("flex items-center h-14 px-4 shrink-0", collapsed && !isMobile && "justify-center px-0")}>
        {(!collapsed || isMobile) ? (
          <NavLink to="/" className="flex items-center gap-1.5">
            <span className="font-brand text-xl tracking-wide text-foreground">
              STRANGELOVE
            </span>
            <span className="font-brand text-xl tracking-wide text-electric-blue">
              OS
            </span>
          </NavLink>
        ) : (
          <NavLink to="/" className="font-brand text-lg text-electric-blue">
            S
          </NavLink>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            title={collapsed && !isMobile ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg text-sm font-medium transition-colors relative",
                collapsed && !isMobile ? "justify-center px-2 py-2.5" : "px-3 py-2.5",
                isActive
                  ? "text-foreground bg-[rgba(75,117,242,0.1)]"
                  : "text-[#8A9A98] hover:text-foreground hover:bg-[rgba(255,255,255,0.05)]"
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* Active accent bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-electric-blue rounded-r-full" />
                )}
                <item.icon className="w-[18px] h-[18px] shrink-0" />
                {(!collapsed || isMobile) && <span>{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Divider + Stats */}
      {(!collapsed || isMobile) && (
        <div className="px-4 py-3 border-t border-[rgba(255,255,255,0.06)]">
          <div className="flex items-center gap-3 text-xs text-[#8A9A98]">
            <div className="flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-mint" />
              <span className="font-mono text-mint">{stats.streak}</span>
              <span>streak</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-electric-yellow" />
              <span className="font-mono text-electric-yellow">{stats.xp}</span>
              <span>XP</span>
            </div>
          </div>
        </div>
      )}

      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center h-10 mx-2 mb-2 rounded-lg text-[#8A9A98] hover:text-foreground hover:bg-[rgba(255,255,255,0.05)] transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#010D0C]">
      {/* Desktop sidebar */}
      <aside
        className="fixed top-0 left-0 bottom-0 z-40 hidden md:block bg-[#0A1A19] border-r border-[rgba(255,255,255,0.06)] transition-all duration-200"
        style={{ width: sidebarWidth }}
      >
        {sidebarContent()}
      </aside>

      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-12 bg-[#0A1A19]/95 backdrop-blur-lg border-b border-[rgba(255,255,255,0.06)] flex items-center px-4 md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="text-[#8A9A98] hover:text-foreground"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-1.5 ml-3">
          <span className="font-brand text-lg tracking-wide text-foreground">
            STRANGELOVE
          </span>
          <span className="font-brand text-lg tracking-wide text-electric-blue">
            OS
          </span>
        </div>
      </div>

      {/* Mobile overlay drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed top-0 left-0 bottom-0 z-50 w-[280px] bg-[#0A1A19] border-r border-[rgba(255,255,255,0.06)] md:hidden"
            >
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute top-3 right-3 text-[#8A9A98] hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
              {sidebarContent(true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content area */}
      <main
        className="min-h-screen transition-all duration-200 pt-12 md:pt-0"
        style={{ marginLeft: `var(--sidebar-width, 0px)` }}
      >
        <style>{`@media (min-width: 768px) { :root { --sidebar-width: ${sidebarWidth}px; } }`}</style>
        <WarRoomBackground>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </WarRoomBackground>
      </main>
    </div>
  );
}
